import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import './CreateDeckPopup.css'; // Importing CSS for the popup

interface DefaultDeck {
  id: string;
  title: string;
  description: string;
}

interface CreateDeckPopupProps {
  onClose: () => void; // Function to close the popup
  onSubmit: () => void; // Function to submit the popup
  selectedLanguage: string; // Selected language passed from parent component
}

const CreateDeckPopup: React.FC<CreateDeckPopupProps> = ({
  onClose,
  onSubmit,
  selectedLanguage,
}) => {
  const [deckName, setDeckName] = useState<string>('');
  const [newLimit, setNewLimit] = useState<number | undefined>(undefined);
  const [reviewLimit, setReviewLimit] = useState<number | undefined>(undefined);
  const [showAdvancedSettings, setShowAdvancedSettings] =
    useState<boolean>(false);
  const [defaultDecks, setDefaultDecks] = useState<DefaultDeck[]>([]);
  const [selectedDefaultDeck, setSelectedDefaultDeck] = useState<string>('');
  const [isViewingStarterDecks, setIsViewingStarterDecks] =
    useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchDefaultDecks = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `/api/decks/default/${selectedLanguage}`
      );
      setDefaultDecks(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching default decks:', error);
      setLoading(false);
    }
  }, [selectedLanguage]);

  useEffect(() => {
    if (selectedLanguage && isViewingStarterDecks) {
      fetchDefaultDecks();
    }
  }, [fetchDefaultDecks, selectedLanguage, isViewingStarterDecks]);

  const handleCreateDeck = async () => {
    try {
      if (isViewingStarterDecks && selectedDefaultDeck) {
        // Create a starter deck using the default deck endpoint
        await axios.post('/api/decks/default', {
          language_code: selectedLanguage,
          id: selectedDefaultDeck,
        });
      } else {
        // Validate normal deck creation
        if (!selectedLanguage || !deckName) {
          alert('Please fill in all required fields');
          return;
        }

        // Create a custom deck
        const payload = {
          name: deckName,
          language_code: selectedLanguage,
          new_limit: showAdvancedSettings ? newLimit : undefined,
          review_limit: showAdvancedSettings ? reviewLimit : undefined,
        };

        await axios.post('/api/decks', payload);
      }
      // Close the popup after successful creation
      onSubmit();
    } catch (error) {
      console.error('Error creating deck:', error);
      onClose();
    }
  };

  const toggleAdvancedSettings = () => {
    setShowAdvancedSettings((prev) => !prev);
  };

  const toggleView = () => {
    setIsViewingStarterDecks((prev) => !prev);
  };

  const handleSelectStarterDeck = (deckId: string) => {
    setSelectedDefaultDeck(deckId === selectedDefaultDeck ? '' : deckId);
  };

  return (
    <div className="phn-popup-overlay">
      <div className="phn-white-card phn-popup" style={{ width: '500px' }}>
        <h2>Create New Deck</h2>

        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '20px',
          }}
        >
          <button
            className="phn-text-button"
            onClick={toggleView}
            style={{ fontSize: '1rem', padding: '0' }}
          >
            {isViewingStarterDecks
              ? 'Create Manual Deck'
              : 'Browse Starter Decks'}
          </button>
        </div>

        {!isViewingStarterDecks ? (
          // Custom deck creation view
          <div className="phn-popup-form">
            <label htmlFor="name">
              Name
              <input
                id="name"
                type="text"
                value={deckName}
                onChange={(e) => setDeckName(e.target.value)}
              />
            </label>

            {showAdvancedSettings && (
              <>
                <label htmlFor="newLimit">
                  Daily New Card Limit
                  <input
                    id="new_limit"
                    type="text"
                    value={newLimit !== undefined ? newLimit.toString() : '5'}
                    onChange={(e) => {
                      const value = e.target.value.trim();
                      if (value === '' || isNaN(parseInt(value))) {
                        setNewLimit(0);
                      } else {
                        setNewLimit(parseInt(value));
                      }
                    }}
                  />
                </label>

                <label htmlFor="reviewLimit">
                  Daily Review Card Limit
                  <input
                    id="reviewLimit"
                    type="text"
                    value={
                      reviewLimit !== undefined ? reviewLimit.toString() : '50'
                    }
                    onChange={(e) => {
                      const value = e.target.value.trim();
                      if (value === '' || isNaN(parseInt(value))) {
                        setReviewLimit(0);
                      } else {
                        setReviewLimit(parseInt(value));
                      }
                    }}
                  />
                </label>
              </>
            )}
            <div className="phn-popup-button-container">
              <button
                className="create-deck-button phn-button"
                onClick={handleCreateDeck}
              >
                Create
              </button>
              <button
                className="create-deck-button phn-cancel-button"
                onClick={toggleAdvancedSettings}
              >
                {showAdvancedSettings ? 'Hide Advanced' : 'Advanced'}
              </button>
              <button
                className="create-deck-button phn-cancel-button"
                onClick={onClose}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          // Starter decks view
          <>
            <div className="starter-decks-container">
              {loading ? (
                <p>Loading starter decks...</p>
              ) : (
                <>
                  {defaultDecks.length === 0 ? (
                    <p>No starter decks available for this language.</p>
                  ) : (
                    <div className="starter-decks-grid">
                      {defaultDecks.map((deck) => (
                        <div
                          key={deck.id}
                          className={`starter-deck-card ${
                            selectedDefaultDeck === deck.id ? 'selected' : ''
                          }`}
                          onClick={() => handleSelectStarterDeck(deck.id)}
                        >
                          <h3>{deck.title}</h3>
                          <p>{deck.description}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="phn-popup-button-container">
              <button
                className="create-deck-button phn-button"
                onClick={handleCreateDeck}
                disabled={!selectedDefaultDeck}
              >
                Add Selected Deck
              </button>
              <button
                className="create-deck-button phn-cancel-button"
                onClick={onClose}
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CreateDeckPopup;
